import 'dayjs/locale/ja'
import '@mantine/core/styles.css'
import '@mantine/carousel/styles.css'
import '@mantine/notifications/styles.css'
import '@mantine/dates/styles.css'
import '@mantine/tiptap/styles.css'

// Mantine DataTableのスタイルを読み込む
import '@mantine/core/styles.layer.css'
import 'mantine-datatable/styles.layer.css'
import '~/root.css'
import '~/index.css'

import {
  ColorSchemeScript,
  Container,
  MantineProvider,
  rem,
} from '@mantine/core'
import { DatesProvider } from '@mantine/dates'
import { Notifications } from '@mantine/notifications'
import { type LoaderFunctionArgs, data } from '@remix-run/node'
import {
  Links,
  Meta,
  type MetaFunction,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useRouteError,
  useRouteLoaderData,
} from '@remix-run/react'
import * as Sentry from '@sentry/remix'
import { withSentry } from '@sentry/remix'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import type { AdminSessionInfo, UserSessionInfo } from '~/authenticator.server'
import { AppLayout } from '~/components/app-layout'
import { getSession } from '~/session.server'

dayjs.locale('ja')

export const headers = () => ({
  'WWW-Authenticate': 'Basic',
})

function handleBasicAuth(request: Request) {
  // ローカルホストと本番環境で基本認証を通す
  if (
    [
      'localhost:5173',
      `${import.meta.env.VITE_PROJECT_YEAR}.syn-ad.com`,
    ].includes(request.headers.get('host') ?? 'unknownHost')
  ) {
    return true
  }
  const authHeader = request.headers.get('Authorization')
  if (!authHeader) {
    return false
  }
  const [scheme, encoded] = authHeader.split(' ')
  if (scheme !== 'Basic' || !encoded) {
    return false
  }
  const decoded = Buffer.from(encoded, 'base64').toString('utf-8')
  const [username, password] = decoded.split(':')
  return username === 'ig' && password === 'S3eZjir8EgiGtkO'
}

export function ErrorBoundary() {
  // 全くどこもキャッチされなかった用の最後の砦
  const error = useRouteError()

  Sentry.captureRemixErrorBoundaryError(error)

  return (
    <html lang='ja'>
      <head>
        <title>エラーです</title>
        <Meta />
        <Links />
      </head>
      <body>
        エラーが発生しました。
        <Scripts />
      </body>
    </html>
  )
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
  if (!handleBasicAuth(request)) {
    return data(
      { basicAuthSuccess: false, user: null, admin: null },
      { status: 401 },
    )
  }

  // AppLayoutのヘッダーに渡したいためログイン中のユーザー・管理者情報を取得
  const session = await getSession(request.headers.get('cookie'))
  const admin: AdminSessionInfo | undefined = session.get('admin')
  const user: UserSessionInfo | undefined = session.get('user')

  return data({
    basicAuthSuccess: true,
    user,
    admin,
  })
}

export const meta: MetaFunction = () => {
  const PROJECT_YEAR = import.meta.env.VITE_PROJECT_YEAR
  const BASE_URL = import.meta.env.VITE_BASE_URL

  return [
    // サイト基本設定,
    {
      name: 'keywords',
      content: `IT,広告,PR,SP,インターネット,Web,就活,就職活動,情報サイト,シンアド就活${PROJECT_YEAR},インターン`,
    },
    {
      tagName: 'link',
      rel: 'canonical',
      href: `${BASE_URL}/`,
    },
    {
      tagName: 'link',
      rel: 'icon',
      href: `${BASE_URL}/favicon.ico`,
    },
    // タッチアイコン
    {
      tagName: 'link',
      rel: 'apple-touch-icon',
      href: `${BASE_URL}/synad_icon.png`,
    },
    {
      tagName: 'link',
      rel: 'manifest',
      href: `${BASE_URL}/site.webmanifest`,
    },
    {
      name: 'apple-mobile-web-app-title',
      content: `シンアド就活${PROJECT_YEAR}`,
    },
    { name: 'application-name', content: `シンアド就活${PROJECT_YEAR}` },

    // OGP
    { property: 'og:type', content: 'website' },
    { property: 'og:locale', content: 'ja_JP' },

    { name: 'twitter:card', content: 'summary' },
    { name: 'twitter:site', content: '@shinsotsu_ad' },
  ]
}

declare global {
  interface Window {
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    dataLayer: any[]
  }
}

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useRouteLoaderData<typeof loader>('root') // rootではuseLoaderDataが使えない https://github.com/remix-run/remix/issues/8951
  const admin = data?.admin ?? null
  const user = data?.user ?? null
  const basicAuthSuccess = data?.basicAuthSuccess ?? false
  const GTM_ID = import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID

  const location = useLocation()
  useEffect(() => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'page_view',
      page_path: location.pathname + location.search,
    })
  }, [location.pathname, location.search])

  useEffect(() => {
    if (!user) return
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      user_id: user.id,
    })
  }, [user])

  return (
    <html lang='ja'>
      <head>
        <meta charSet='utf-8' />
        <meta
          name='viewport'
          content='width=device-width,initial-scale=1,viewport-fit=cover'
        />
        <link
          rel='stylesheet'
          href='https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;700&display=swap'
        />
        {process.env.NODE_ENV === 'production' && GTM_ID && (
          <script
            // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
            dangerouslySetInnerHTML={{
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${GTM_ID}');`,
            }}
          />
        )}
        <Meta />
        <Links />
        <ColorSchemeScript
          defaultColorScheme={import.meta.env.VITE_COLOR_SCHEME ?? 'light'}
        />
      </head>
      <body>
        {process.env.NODE_ENV === 'production' && (
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
              height='0'
              width='0'
              style={{ display: 'none', visibility: 'hidden' }}
              title='Google Tag Manager'
            />
          </noscript>
        )}
        <MantineProvider
          defaultColorScheme={import.meta.env.VITE_COLOR_SCHEME ?? 'light'}
          forceColorScheme={import.meta.env.VITE_COLOR_SCHEME ?? 'light'}
          theme={{
            fontFamily: 'Noto Sans JP, sans-serif',
            headings: {
              fontFamily: 'Noto Sans JP, sans-serif',
              fontWeight: '400',
              sizes: {
                h1: {
                  fontWeight: '700',
                  fontSize: '1.75rem',
                },
                h2: {
                  fontWeight: '700',
                  fontSize: '1.5rem',
                },
                h3: {
                  fontWeight: '700',
                  fontSize: '1.25rem',
                },
                h4: {
                  fontWeight: '700',
                  fontSize: '1rem',
                },
                h5: {
                  fontWeight: '700',
                  fontSize: '0.75rem',
                },
                h6: {
                  fontWeight: '400',
                  fontSize: '0.5rem',
                },
              },
            },
            fontSizes: {
              xs: '10px',
              sm: '12px',
              md: '14px',
              lg: '16px',
              xl: '18px',
            },
            lineHeights: {
              xs: '18px',
              sm: '22px',
              md: '26px',
              lg: '28px',
              xl: '30px',
            },
            breakpoints: {
              xs: '320px',
              sm: '660px',
              md: '800px',
              lg: '1040px',
              xl: '1366px',
            },
            colors: {
              primary: [
                '#D4E2EE',
                '#ACC7DF',
                '#83ACCF',
                '#5B90BF',
                '#3275B0',
                '#0A5AA0',
                '#084478',
                '#052D51',
                '#031729',
                '#000102',
              ],
              secondary: [
                '#E9E9E9',
                '#D4D4D4',
                '#BFBFBF',
                '#AAAAAA',
                '#959595',
                '#808080',
                '#606060',
                '#414141',
                '#212121',
                '#000000',
              ],
              tertiary: [
                '#EEEFF2',
                '#DDDFE7',
                '#CDD0DB',
                '#BDC1CF',
                '#ACB1C3',
                '#9CA2B7',
                '#757A8A',
                '#4F525C',
                '#282A2F',
                '#020202',
              ],
              error: [
                '#FBDAD5',
                '#F7B8AC',
                '#F29584',
                '#EE725C',
                '#EA5034',
                '#E62D0C',
                '#AD2209',
                '#741706',
                '#3B0C03',
                '#020000',
              ],
              success: [
                '#D3F5E1',
                '#AAECC5',
                '#81E3A9',
                '#58DA8D',
                '#2FD071',
                '#06C755',
                '#059640',
                '#03652B',
                '#023316',
                '#000201',
              ],
            },
            primaryColor: 'primary',
            other: {
              fontWeights: {
                bold: 700,
                extraBold: 900,
              },
            },
            components: {
              Container: Container.extend({
                vars: (_, { size, fluid }) => {
                  const containerSizes: Record<string, string> = {
                    xs: '320px',
                    sm: '660px',
                    md: '800px',
                    lg: '1040px',
                    xl: '1366px',
                  }

                  return {
                    root: {
                      '--container-size': fluid
                        ? '100%'
                        : size !== undefined && size in containerSizes
                          ? containerSizes[size]
                          : rem(size),
                    },
                  }
                },
              }),
              Tooltip: {
                defaultProps: {
                  arrow: 'true',
                  multiline: true,
                  withArrow: true,
                  transition: 'fade',
                  position: 'top',
                  color: 'white',
                  arrowSize: 8,
                },
                styles: {
                  tooltip: {
                    textAlign: 'center',
                    boxShadow:
                      '0px 6px 14px 0px var(--mantine-color-secondary-1)',
                    border: '1px solid var(--mantine-color-secondary-2)',
                    color: 'var(--mantine-color-primary-6)',
                    fontSize: 'md',
                    fontWeight: 'bold',
                  },
                  arrow: {
                    border: '1px solid var(--mantine-color-secondary-2)',
                  },
                },
              },
              Breadcrumbs: {
                styles: {
                  breadcrumb: {
                    whiteSpace: 'wrap',
                  },
                },
              },
            },
            spacing: {
              xs: '8px',
              sm: '12px',
              md: '16px',
              lg: '20px',
              xl: '32px',
            },
          }}
        >
          <DatesProvider
            settings={{
              locale: 'ja',
              firstDayOfWeek: 0,
              weekendDays: [0],
              timezone: 'JST',
            }}
          >
            <Notifications
              position='bottom-center'
              styles={{
                notification: {
                  height: '5vh',
                  fontSize: 'md',
                },
              }}
            />
            {basicAuthSuccess ? (
              <AppLayout userInfo={user} adminUserInfo={admin}>
                {children}
              </AppLayout>
            ) : (
              <div>These aren't the droids you are looking for</div>
            )}
          </DatesProvider>
        </MantineProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

function App() {
  return <Outlet />
}

export default withSentry(App)
